import {
  createContext,
  useContext,
  useEffect,
  useRef
} from "react";

/**
 * Interface defining the public API of the audio manager.
 * All children of AudioManager can access these methods via the `usePageAudio` hook.
 */
export interface PageAudioContextValue {
  /** Starts playback of the audio. Returns a Promise because playback may be blocked by the browser until a user gesture. */
  play: () => Promise<void>;

  /** Pauses the audio playback. */
  pause: () => void;

  /**
   * Sets the audio volume.
   * @param v - volume level between 0.0 (silent) and 1.0 (max)
   */
  setVolume: (v: number) => void;
}

/** Internal React context to share the audio API across children */
const PageAudioContext = createContext<PageAudioContextValue | null>(null);

/**
 * Props for the AudioManager component.
 */
interface AudioManagerProps {
  /** Path or URL to the audio file to play */
  src: string;

  /** Whether the audio should loop continuously */
  loop?: boolean;

  /** Initial volume level (0.0 to 1.0) */
  initialVolume?: number;

  /** React children components that will have access to the audio API */
  children: React.ReactNode;
}

/**
 * AudioManager component provides a page-scoped audio context.
 * 
 * Usage:
 * ```tsx
 * <AudioManager src="/audio/song.mp3" loop initialVolume={0.5}>
 *   <MyPageContent />
 * </AudioManager>
 * ```
 * Children can then use `usePageAudio()` to control playback.
 */
export const AudioManager: React.FC<AudioManagerProps> = ({
  src,
  loop = true,
  initialVolume = 0.5,
  children
}) => {
  /** Ref to store the HTMLAudioElement */
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(src);
    audio.loop = loop;
    audio.volume = initialVolume;

    audioRef.current = audio;

    // Cleanup: pause audio and release reference
    return () => {
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, [src, loop, initialVolume]);

  /** Value provided to context consumers */
  const value: PageAudioContextValue = {
    play: () => audioRef.current!.play(),
    pause: () => audioRef.current!.pause(),
    setVolume: (v) => {
      if (audioRef.current) audioRef.current.volume = v;
    }
  };

  return (
    <PageAudioContext.Provider value={value}>
      {children}
    </PageAudioContext.Provider>
  );
};

/**
 * Hook to access the page-scoped audio API.
 * Must be used inside a component wrapped by `AudioManager`.
 * 
 * Example:
 * ```tsx
 * const { play, pause, setVolume } = usePageAudio();
 * ```
 * @throws Error if used outside an AudioManager
 */
export const usePageAudio = (): PageAudioContextValue => {
  const ctx = useContext(PageAudioContext);
  if (!ctx) {
    throw new Error("usePageAudio must be used inside AudioManager");
  }
  return ctx;
};
